import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Tenant role row returned by `GET /api/TenantRoles/Get/{organizationId}`.
 *
 * `permissiongrp` stays optional on the type: the lean endpoint does not return
 * it, and no consumer of this hook reads it. It is kept so the type still
 * describes rows obtained from the by-id endpoint elsewhere.
 *
 * Powers the wizard's ROLE dropdown when creating practitioners (typeId=3) or
 * staff (typeId=2). The numeric `tenantId` is the SAME enum as the typeId in
 * the path (3=practitioner, 2=staff) — distinct from the rich `permissiongrp`
 * payload below.
 *
 * Captured against dev 2026-05-21 (typeId=3 → "In-house Practitioner"
 * roleId=6, "Visiting Practitioner" roleId=18). Other fields like
 * `permissiongrp` are heavy and unused for a dropdown — included on the
 * type for parity but consumers should map to `{roleId, roleName}` only.
 */
/**
 * One suggested speciality. Shaped to convert cleanly into the `StaffSpeciality`
 * the save expects, so a chip and a typeahead pick are indistinguishable
 * downstream -- see `suggestionToSpeciality`.
 */
export interface SpecialitySuggestion {
  specialitySkillId: string;
  code: string | null;
  system: string | null;
  display: string | null;
  sortOrder?: number | null;
}

/**
 * One suggested speciality. Shaped to convert cleanly into the `StaffSpeciality`
 * the save expects, so a chip and a typeahead pick are indistinguishable
 * downstream -- see `suggestionToSpeciality`.
 */
export interface SpecialitySuggestion {
  specialitySkillId: string;
  code: string | null;
  system: string | null;
  display: string | null;
  sortOrder?: number | null;
}

export interface TenantRoleRow {
  roleId: number;
  tenantId: number;
  organizationId: string;
  roleName: string;
  roleDescription: string;
  isArchived: boolean;
  isNurse: boolean;
  /**
   * `TenantMasters.Id` -- the GLOBAL catalog id, distinct from `roleId`
   * (which is `TenantRoles.Id`, a per-organisation row id). This is the only
   * stable way to recognise a role across orgs, since `roleName` is editable
   * per org and three of eight production organisations have renamed theirs.
   *
   * OPTIONAL because the server does not publish it yet: the Identity change
   * that adds `string TenantMasterId = 8` to the gRPC message and this REST
   * response is written but not deployed. Consumers must handle it being
   * absent and fall back to a name match -- see `staffRoleOptions`.
   *
   * Typed as string OR number because the two transports disagree, and both
   * reach a client:
   *   - REST  (`TenantRoleNewDto.TenantMasterId`) is C# `int?` -> a JSON NUMBER.
   *   - gRPC  (`identity.proto`) declares it `string`, matching the two sibling
   *     ids already on that message, where `0` cannot be told from absent.
   * Consumers must not assume either; coerce with `Number(...)` and check for
   * NaN. See `staffRoleOptions`.
   */
  tenantMasterId?: string | number | null;
  /**
   * Specialities to offer as CHIPS for this profession, in display order
   * (`dbo.RoleSpecialitySuggestions`, script 011). Absent or empty means this
   * profession has no curated list -- which is the normal state for everything
   * except Allied Health today -- and the speciality field then behaves exactly
   * as it always has.
   *
   * These SUGGEST. The speciality typeahead is never filtered by them, so no
   * speciality is ever made unreachable.
   */
  suggestedSpecialities?: SpecialitySuggestion[] | null;
  permissiongrp?: unknown[];
}

/**
 * Tenant role types per `app_constants.dart` (Flutter `FetchRoleByTypeEvent(id: N)`):
 *   2 = staff roles (used by PR 5 Add Staff)
 *   3 = practitioner/doctor roles (used by PR 4 Add Practitioner)
 */
export type TenantRoleType = 2 | 3;

/**
 * Fetch tenant-role rows filtered by role type. Flutter parity:
 * `fetchRolesByType` in `settings_data_source_impl.dart:1151-1163` —
 *   GET /api/TenantRoles/Get/{organizationId}
 *
 * THIS USED TO CALL `GetRole/{typeId}`, ONCE PER BUCKET, AND THAT WAS THE
 * SLOWEST THING ON THE PRACTITIONER SCREEN. That endpoint returns a
 * `permissiongrp` tree per role; no caller of THIS hook reads it — they all
 * render role pickers. Measured in the browser: 4.9s for bucket 3 and 7.6s for
 * bucket 2, ~55KB of permissions to populate a dropdown, and because Identity
 * serialises role reads the two also stalled the grouped practitioner call from
 * 1.7s to 4.8s. Total screen time 8.3s (PROD-2359).
 *
 * `Get/{organizationId}` returns every role in the organisation — both buckets,
 * with `tenantId` on each row — lean, in ~0.6s. The bucket filter is applied in
 * `select` rather than by the server.
 *
 * `useRolesList` already used this exact endpoint, so this hook now shares its
 * cache entry: one request serves the Roles tab, the pickers, and both buckets.
 *
 * DO NOT repoint this back at `GetRole/{typeId}` to obtain `permissiongrp`.
 * Consumers that genuinely need permissions (the logs filter, the roles page)
 * use `useRolesList`/the by-id endpoint; adding permissions here would put a
 * 55KB tree behind every dropdown again.
 *
 * Hook callers should filter `!isArchived` and project to
 * `{ value: roleId, label: roleName }` for dropdown rendering.
 */
export function useRolesByType(
  typeId: TenantRoleType,
  organizationId: string | null | undefined,
) {
  return useQuery({
    // SAME KEY AND SAME REQUEST AS useRolesList, DELIBERATELY. Every consumer of
    // role data on a page now shares ONE fetch: a screen asking for both buckets
    // makes one call, not two, and it is already warm if the Roles tab loaded it.
    queryKey: ['settings', 'roles-list', organizationId],
    queryFn: ({ signal }) => {
      if (!organizationId) {
        throw new Error('useRolesByType called without organizationId');
      }
      return get<TenantRoleRow[]>(
        `/api/TenantRoles/Get/${organizationId}`,
        { signal },
      );
    },
    // The bucket filter moves to `select`: one cached response, cheaply derived
    // per caller. `select` does not refetch, so asking for bucket 3 and bucket 2
    // on the same screen costs exactly one network call.
    select: (rows: TenantRoleRow[]) =>
      (rows ?? []).filter((r) => r.tenantId === typeId),
    enabled: !!organizationId,
  });
}
